import { useCallback, useEffect, useState } from "react";
import { FormPayloadV2 } from "../../data/drugOptionsV2";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import * as actions from "../../store/slices/HealthCoachSlice";
import { constants } from "../../constants/common";
import { toast } from "react-toastify";
import { parseNextCycleDateForDateInput } from "../../utils/date-and-time";

interface UseScheduleCycleV2Props {
  showUploadDocuments: (formData: FormPayloadV2) => void;
  detailsObject: any;
}

const useScheduleCycleV2 = ({
  showUploadDocuments,
  detailsObject,
}: UseScheduleCycleV2Props) => {
  const dispatch = useDispatch();
  const {
    scheduleCycleState: { isLoading, errors, data },
  } = useSelector((state: RootState) => state.healthCoach);
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<FormPayloadV2>({
    // Primary Drug Fields
    primaryDrug: "",
    primaryDrugPaidStatus: 0, // Default to Paid
    
    // Secondary Drug Fields - Now array of objects with individual status
    secondaryDrugs: [],
    
    // Cancer and Therapy Details
    cancerType: "",
    therapy: "",
    indication: "",
    
    // Cycle/Session Details
    cycle_number: "",
    upcoming_cycle_date: "",
    upcoming_dosage: "",
    notes: "",
    brain_mets: 0,
    
    // Rewards
    isRewardsAssign: false,
  });

  useEffect(() => {
    if (!isLoading) {
      if (errors) {
        toast.error(errors || constants.ERROR_MESSAGES.SOMETHING_WENT_WRONG);
      }
      if (data) {
        navigateToVerifiedPatients();
        dispatch(actions.resetScheduleCycle());
      }
    }
  }, [isLoading, errors, data]);

  const navigateToVerifiedPatients = () => {
    const token = detailsObject.token ?? localStorage.getItem("token");
    navigate(`/all-patients/:token`, {
      state: {
        token: token,
        journey: "verified-patients",
      },
    });
  };

  const scheduleCycle = () => {
    // Build primary_drugs array with is_free inside each drug object
    const primaryDrugs = [
      {
        drug_name: formData.primaryDrug,
        is_free: String(formData.primaryDrugPaidStatus || 0), // Ensure string: "0" = Paid, "1" = Free
      },
    ];

    // Build secondary_drugs array if any are selected
    // Each drug now has its own is_free status
    // Filter out empty rows and map to clean objects with only drug_name and is_free
    const secondaryDrugs =
      formData.secondaryDrugs && formData.secondaryDrugs.length > 0
        ? formData.secondaryDrugs
            .filter((drug) => drug.drug_name && drug.drug_name !== "")
            .map((drug) => ({
              drug_name: drug.drug_name,
              is_free: String(drug.is_free !== undefined ? drug.is_free : 0), // Ensure string
            }))
        : [];
    
    const hasSecondaryDrugs = secondaryDrugs.length > 0;

    // Build visit_meta_info object
    const visitMetaInfo: any = {
      target_therapy_drug: formData.primaryDrug,
      primary_drugs: primaryDrugs,
      therapy_type: formData.therapy,
      visit_cycle: "1",
      cancer_type: formData.cancerType,
      notes: formData.notes,
      brain_mets: formData.brain_mets,
      // Note: is_primary_drug_free removed - is_free is now inside primary_drugs array
    };

    // Only include therapy_indication if it has a value (not empty string)
    // This means the therapy has indications and user selected one
    if (formData.indication && formData.indication !== "") {
      visitMetaInfo.therapy_indication = formData.indication;
    }

    // Add dosage for Nivolumab
    if (formData.primaryDrug === "nivolumab" && formData.upcoming_dosage) {
      visitMetaInfo.dosage = formData.upcoming_dosage;
    }

    // Add secondary drugs array if any are selected
    // Each drug object already contains drug_name and is_free
    if (hasSecondaryDrugs) {
      visitMetaInfo.secondary_drugs = secondaryDrugs;
    }

    const payload = {
      patient_id: detailsObject.patientData.patient_id,
      patient_phone: detailsObject.patientData.contact_no,
      patient_name: detailsObject.patientData.name,
      updated_by: detailsObject.userDetails.health_coach_id,
      follow_up_date: formData.upcoming_cycle_date,
      support_program_drug: formData.primaryDrug,
      visit_meta_info: visitMetaInfo,
      ...(formData.isRewardsAssign
        ? {
            assign_reward: 1,
            assign_reward_params: {
              amount: parseInt(`${detailsObject.coins}00`),
              transactionType: "DOCUMENT_UPLOAD",
              notes: {
                type: "visit_status_approved",
              },
              patientId: detailsObject.patientData.patient_id,
            },
          }
        : { assign_reward: 0 }),
    };

    dispatch(actions.scheduleCycleRequest(payload));
  };

  const handleTypeChange = (e: any) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, cycle_number: value }));
  };

  const handleAddMoreDetails = () => {
    if (formData.cycle_number === "0") {
      setIsModalOpen(true);
    } else {
      showUploadDocuments(formData);
    }
  };

  /**
   * Maps OCR keys from GroupComponent (primary_drug, secondary_drug, cancer_type, next_cycle_date, …)
   * into {@link FormPayloadV2} for the create-cycle form.
   */
  const applyOcrToScheduleForm = useCallback((data: Record<string, string>) => {
    setFormData((prev) => {
      const incomingPrimary =
        data?.primary_drug?.trim?.()?.toLowerCase() ?? data?.primary_drug ?? "";
      const primaryChanged =
        Boolean(incomingPrimary) && incomingPrimary !== prev?.primaryDrug;

      let secondaryDrugs = prev.secondaryDrugs;
      if (primaryChanged) {
        secondaryDrugs = data?.secondary_drug
          ? [{ drug_name: data.secondary_drug, is_free: 0 as const }]
          : [];
      } else if (data?.secondary_drug) {
        secondaryDrugs = [{ drug_name: data.secondary_drug, is_free: 0 as const }];
      }

      let primaryDrugPaidStatus = prev.primaryDrugPaidStatus ?? 0;
      const rawPf = data?.is_primary_drug_free;
      if (rawPf !== undefined && rawPf !== "") {
        const n = parseInt(String(rawPf), 10);
        if (Number.isFinite(n)) {
          primaryDrugPaidStatus = n === 1 ? 1 : 0;
        }
      }

      const parsedNextCycleDate = parseNextCycleDateForDateInput(
        data?.next_cycle_date
      );

      return {
        ...prev,
        primaryDrug: incomingPrimary || prev.primaryDrug,
        cancerType: data?.cancer_type || prev.cancerType,
        upcoming_cycle_date: parsedNextCycleDate ?? prev.upcoming_cycle_date,
        therapy: data?.therapy_type || prev.therapy,
        indication: data?.therapy_indication || prev.indication,
        primaryDrugPaidStatus,
        secondaryDrugs,
        upcoming_dosage: data?.upcoming_dosage || prev.upcoming_dosage,
      };
    });
  }, []);

  return {
    data,
    errors,
    formData,
    isLoading,
    isModalOpen,
    setFormData,
    scheduleCycle,
    setIsModalOpen,
    handleTypeChange,
    handleAddMoreDetails,
    applyOcrToScheduleForm,
  };
};

export default useScheduleCycleV2;
